import { type FC, type MouseEvent, type KeyboardEvent, useCallback, useMemo, useState } from 'react';

import { Button } from '@vakantiesnl/components/src/atoms/Button';
import { Disclaimer } from '@vakantiesnl/components/src/atoms/Disclaimer';
import { Divider } from '@vakantiesnl/components/src/atoms/Divider';
import { getTogglePayloadValue, hasEnabledFeature } from '@vakantiesnl/components/src/atoms/FeatureToggle/utils';
import { Tag } from '@vakantiesnl/components/src/atoms/Tag';
import { Typography } from '@vakantiesnl/components/src/atoms/Typography';
import { AdlinksContainer } from '@vakantiesnl/components/src/molecules/AdlinksContainer';
import { OfferDetails, hasOfferData } from '@vakantiesnl/components/src/molecules/OfferDetails/OfferDetails';
import { isZoover } from '@vakantiesnl/components/src/utils';
import {
	AccommodationSections,
	MENU_SCROLL_DURATION,
	MENU_OFFSET,
} from '@vakantiesnl/components/src/utils/accoSections';
import { generateDisclaimerPriceMessage } from '@vakantiesnl/components/src/utils/disclaimerPriceMessage';
import { useBreakpoints } from '@vakantiesnl/components/src/utils/useBreakpoints';
import { useMicroCopyContext } from '@vakantiesnl/services/src/context/microCopyContext';
import { useTripFilterContext } from '@vakantiesnl/services/src/context/useTripFilterContext';
import type { AdlinkValues } from '@vakantiesnl/services/src/gtm/adlinks';
import { useApplicationStore } from '@vakantiesnl/services/src/stores/applicationStore';
import { isAccoOnlyOffer, isPackageOffer } from '@vakantiesnl/services/src/util';
import { getDateStringWithoutYear } from '@vakantiesnl/services/src/util/dateUtils';
import { getFormattedRoundedPrice } from '@vakantiesnl/services/src/util/mathUtils';
import { inject } from '@vakantiesnl/services/src/util/template';
import { type Search, type Adlinks as AdlinkTypes } from '@vakantiesnl/types';
import dayjs from 'dayjs';
import { InView } from 'react-intersection-observer';
import { scroller } from 'react-scroll';

import { useCustomStyles } from '../PriceCard.style';
import { PriceCardSkeleton } from '../PriceCardSkeleton/PriceCardSkeleton';

type Props = {
	/** Offer item to display the price */
	offer?: Search.OfferItem | Search.AccommodationOnlyOffer | null;
	/** Whether the fetching of current offer data is loading */
	offerIsLoading?: boolean;
	/** Whether there are offers available */
	hasOffers: boolean;
	/** View CTA onClick tracking event */
	viewOffersEvent: (clickEvent: React.MouseEvent<HTMLButtonElement>) => void;
	/** Gallery is open */
	isGalleryOpen?: boolean;
	/** Modal is open */
	isModalOpen?: boolean;
	/** The array containing the adlinks for that acco */
	adlinks?: AdlinkTypes.Adlink[];
	/** Handler for adlink CTA click */
	handleAdlinkCtaClick?: (clickEvent: MouseEvent<HTMLButtonElement>) => void;
	/** Tracking function for adlink click */
	onAdlinkClick: (adlinkValues: AdlinkValues) => void;
	/** Wether the price of the pricecard is in the viewport */
	setIsPriceInView?: (inView: boolean) => void;
	/** Acco data for tracking events */
	accommodation: Search.FullAccommodationItem;
	/** Wether the acco is saved in the favorites */
	isFavorite?: boolean;
	onFavoriteClick?: () => void;
};

const DOWNPAYMENT_WEEKS_THRESHOLD = 6;

export const BookableView: FC<Props> = ({
	offer,
	offerIsLoading,
	viewOffersEvent,
	isGalleryOpen,
	isModalOpen,
	adlinks,
	handleAdlinkCtaClick,
	onAdlinkClick,
	setIsPriceInView,
	accommodation,
	isFavorite,
	onFavoriteClick,
	hasOffers,
}) => {
	const { classes, cx } = useCustomStyles();
	const microCopies = useMicroCopyContext();
	const { isMobile, isDesktop, isTabletOrIsDesktop } = useBreakpoints();
	const featuresState = useApplicationStore((s) => s.features);

	/** Hide adlinks content by default */
	const [showAdlinksContent, setShowAdlinksContent] = useState(true);

	const offerData = useMemo(
		() =>
			isPackageOffer(offer)
				? {
						departureAirport: offer?.outbound.departureAirport,
						mealPlanCode: offer?.rooms?.[0].board.code,
						departureDate: offer?.departureDateYYYYMMDD,
						hasTransferIncluded: offer?.hasTransferIncluded,
						hasLuggageIncluded: offer?.hasLuggageIncluded,
						durationDays: offer?.durationDays,
						durationNights: offer?.durationNights,
					}
				: {
						departureDate: offer?.departureDateYYYYMMDD,
						roomName: offer?.productInfo?.name,
						durationDays: offer?.durationDays,
						durationNights: offer?.durationNights,
						mealPlanCode: 'AO',
						isAccoOnly: true,
					},

		[offer],
	);

	const showPrice = useMemo(
		() => !isModalOpen || (isModalOpen && !isGalleryOpen) || (isGalleryOpen && !isMobile && offer),
		[isGalleryOpen, isMobile, isModalOpen, offer],
	);

	const showExtendedView = useMemo(
		() => !isModalOpen || (isModalOpen && !isGalleryOpen) || (isGalleryOpen && isDesktop),
		[isDesktop, isGalleryOpen, isModalOpen],
	);

	const useFlatDownpayment = useMemo(() => {
		return hasEnabledFeature(featuresState, 'exp-flat-downpayment');
	}, [featuresState]);

	const downpaymentApplicable = useMemo(() => {
		return dayjs(offerData.departureDate).diff(dayjs(), 'weeks') >= DOWNPAYMENT_WEEKS_THRESHOLD;
	}, [offerData.departureDate]);

	const downpaymentMicrocopy = useMemo(() => {
		const payloadValueStr = getTogglePayloadValue(featuresState, 'exp-flat-downpayment');
		const payloadValueNum = parseInt(payloadValueStr);

		let downpaymentValue = '';

		if (!Number.isNaN(payloadValueNum)) {
			downpaymentValue = String(payloadValueNum / 100);
		}

		const variables = {
			downpaymentValue,
		};
		return inject(microCopies['pricecard.flatDownpayment'] || '', variables).join('');
	}, [featuresState, microCopies]);

	const handleAdlinkTitleToggle = useCallback(() => {
		/** Local state of the state so it's not conflicting with the actual state being set later */
		const shouldShowAdlinksContent = !showAdlinksContent;
		setShowAdlinksContent(shouldShowAdlinksContent);

		import('@vakantiesnl/services/src/gtm/common').then(({ track }) =>
			import('@vakantiesnl/services/src/gtm/adlinks').then(({ trackAdlinksToggleAccordionClick }) =>
				track(trackAdlinksToggleAccordionClick(accommodation, shouldShowAdlinksContent, adlinks?.length)),
			),
		);
	}, [accommodation, adlinks?.length, showAdlinksContent]);

	const handleAdlinkTitleKeyDown = useCallback(
		(event: KeyboardEvent<HTMLDivElement>) => {
			event.preventDefault();
			if (event.key === 'Enter' || event.key === ' ') {
				handleAdlinkTitleToggle();
			}
		},
		[handleAdlinkTitleToggle],
	);

	const originalPricePerAdult = useMemo(() => {
		if (isPackageOffer(offer)) {
			return offer.originalPricePerAdult;
		}

		return undefined;
	}, [offer]);

	/**
	 * Price to show in the price card
	 * @Peakwork offer show $pricePerAdult
	 * @AccoOnly offer show $priceTotal
	 */
	const displayPrice = useMemo(() => {
		if (isPackageOffer(offer)) return offer.pricePerAdult;
		else if (isAccoOnlyOffer(offer)) return offer.priceTotal;
	}, [offer]);

	/** Some temporary hacky solution to show a kind of accordion like behavior for the adlinks title and container */
	const AdlinkToggleTitle = useCallback(() => {
		return (
			<>
				<div
					onClick={handleAdlinkTitleToggle}
					onKeyDown={handleAdlinkTitleKeyDown}
					className={classes.adlinksToggleTile}
					role="button"
					tabIndex={0}
				>
					<Typography variant="headingSm" as="h5" className={classes.hybridAdlinkBlockTitle}>
						{microCopies['adlinks.hybridproductpage.heading']}
					</Typography>
					<Button
						leadingIcon={showAdlinksContent ? 'chevronUp' : 'chevronDown'}
						variant="tertiary"
						size="small"
					/>
				</div>
				{isMobile && !showAdlinksContent && <Divider className={classes.adlinksToggleDivider} />}
			</>
		);
	}, [
		classes.adlinksToggleDivider,
		classes.adlinksToggleTile,
		classes.hybridAdlinkBlockTitle,
		handleAdlinkTitleKeyDown,
		handleAdlinkTitleToggle,
		isMobile,
		microCopies,
		showAdlinksContent,
	]);

	const handleOnViewOfferClick = useCallback(
		(e: MouseEvent<HTMLButtonElement>) => {
			viewOffersEvent(e);

			scroller.scrollTo(AccommodationSections.Availability, {
				duration: MENU_SCROLL_DURATION,
				smooth: true,
				offset: MENU_OFFSET,
			});
		},
		[viewOffersEvent],
	);

	const {
		state: { departureDate },
	} = useTripFilterContext();

	const hasNoDepartureDateMatch = !!departureDate?.length && hasOffers && !offer;

	const buttonLabel = useMemo(() => {
		if (displayPrice !== undefined) {
			return microCopies['priceCard.bookable.button'];
		}

		if (hasNoDepartureDateMatch) {
			return microCopies['priceCard.viewAvailableOffers.button'];
		}

		return microCopies['filters.adjustFilters'];
	}, [displayPrice, hasNoDepartureDateMatch, microCopies]);

	if (offerIsLoading) return <PriceCardSkeleton />;

	return (
		<>
			{showPrice && (
				<div className={classes.priceContainer} data-cy="accommodation-PriceCard-price">
					{displayPrice !== undefined && (
						<Typography variant="bodySm" as="p">
							{isPackageOffer(offer)
								? microCopies['common.perPersonFrom']
								: microCopies['common.from'] + '*'}
						</Typography>
					)}
					<InView as="div" rootMargin={'72px 0px 0px 0px'} threshold={0.1} onChange={setIsPriceInView}>
						{displayPrice !== undefined ? (
							<div className={classes.prices}>
								<Typography
									variant={'displayXl'}
									as="div"
									className={classes.price}
									data-cy="accommodation-PriceCard-price-value"
								>
									{getFormattedRoundedPrice(displayPrice)}
								</Typography>
								{originalPricePerAdult && (
									<span className={classes.originalPricePerAdult}>
										{getFormattedRoundedPrice(originalPricePerAdult)}
									</span>
								)}
							</div>
						) : (
							<div
								className={cx(classes.prices, classes.placeholderContainer, {
									[classes.placeHolderDateContainer]: hasNoDepartureDateMatch,
								})}
							>
								<Typography
									variant={'bodySm'}
									as="div"
									className={hasNoDepartureDateMatch ? undefined : classes.placeholderText}
									data-cy="accommodation-PriceCard-price-value"
								>
									{hasNoDepartureDateMatch
										? inject(microCopies['accommodation.viewAvailableOffers.description'], {
												date: getDateStringWithoutYear(new Date(departureDate[0])),
											}).join('')
										: microCopies['accommodation.adjustFilters.description']}
								</Typography>
							</div>
						)}
					</InView>
					{useFlatDownpayment && downpaymentApplicable && (
						<Tag size="small" color="teal">
							{downpaymentMicrocopy}
						</Tag>
					)}

					{!!offer?.extras && (
						<Disclaimer>
							{generateDisclaimerPriceMessage(offer.extras, microCopies, accommodation.isBookableQenner)}
						</Disclaimer>
					)}
				</div>
			)}
			<div className={classes.buttonContainer}>
				<Button
					id="priceCardBookBtn"
					className={classes.checkPriceButton}
					size={isTabletOrIsDesktop ? 'medium' : 'small'}
					aria-label={buttonLabel}
					onClick={handleOnViewOfferClick}
				>
					{buttonLabel}
				</Button>
				{isZoover && (
					<Button
						leadingIcon={isFavorite ? 'favoriteFilled' : 'favorite'}
						className={cx(classes.favoriteButton, { [classes.favoriteButtonActive]: isFavorite })}
						variant="secondary"
						size={isTabletOrIsDesktop ? 'medium' : 'small'}
						onClick={onFavoriteClick}
					/>
				)}
			</div>
			{showExtendedView && (
				<>
					{!offerIsLoading && hasOfferData(offerData) && <Divider />}
					<div className={classes.offerSummaryContainer} data-cy="accommodation-PriceCard-summary">
						{!offerIsLoading && hasOfferData(offerData) ? (
							<OfferDetails microCopies={microCopies} offer={offerData} />
						) : null}
						{!!adlinks?.length && (
							<AdlinksContainer
								adlinks={adlinks}
								onToggleClick={handleAdlinkCtaClick}
								toggleVariant="secondary"
								titleToShow={
									<>
										<Divider />
										<AdlinkToggleTitle />
									</>
								}
								showDefaultDescription={false}
								showFavorite={false}
								onAdlinkClick={onAdlinkClick}
								className={classes.adlinksContainer}
								showAdlinksContent={showAdlinksContent}
							/>
						)}
					</div>
				</>
			)}
		</>
	);
};
